/**
 * In-person volunteers (VSC) CRUD API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { PaginatedResponse } from '@/types/data-table.types';
import type { GetUsersParams, UserWithProfile, WithPhoneSetupLink } from '@/types/user.types';
import type { Volunteer, CreateVolunteerDto, UpdateVolunteerDto } from '@/types/volunteer.types';
import { toLocalDateString } from '@/utils/format-date-utils';

/** Normalize a Date or ISO string to `YYYY-MM-DD` for list query params. */
function toQueryDate(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return toLocalDateString(value);
  }
  if (typeof value === 'string') {
    const ymd = value.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  }
  return undefined;
}

/** `POST /users/invite` — invite a VSC. */
export const createVolunteer = async (dto: CreateVolunteerDto): Promise<Volunteer> => {
  const response = await apiClient.post('/users/invite', dto);
  return response.data.user;
};

/** `PATCH /users/:id` */
export const updateVolunteer = async (
  id: string,
  dto: UpdateVolunteerDto,
): Promise<WithPhoneSetupLink<Volunteer>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<Volunteer>>(`/users/${id}`, dto);
  return response.data;
};

/** `GET /users/volunteers` — paginated volunteers. */
export const getVolunteers = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.levelIds?.forEach((id) => queryParams.append('levelIds', id));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));

  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  const dateFrom = toQueryDate(params?.dateFrom);
  const dateTo = toQueryDate(params?.dateTo);
  if (dateFrom) queryParams.append('dateFrom', dateFrom);
  if (dateTo) queryParams.append('dateTo', dateTo);
  params?.availabilityDays?.forEach((d) => queryParams.append('availabilityDays', String(d)));
  params?.vscFrequencies?.forEach((f) => queryParams.append('vscFrequencies', f));
  if (params?.engagementStartDateFrom)
    queryParams.append('engagementStartDateFrom', params.engagementStartDateFrom);
  if (params?.engagementStartDateTo)
    queryParams.append('engagementStartDateTo', params.engagementStartDateTo);
  if (params?.engagementEndDateFrom)
    queryParams.append('engagementEndDateFrom', params.engagementEndDateFrom);
  if (params?.engagementEndDateTo)
    queryParams.append('engagementEndDateTo', params.engagementEndDateTo);
  if (params?.tutorExitDateFrom) queryParams.append('tutorExitDateFrom', params.tutorExitDateFrom);
  if (params?.tutorExitDateTo) queryParams.append('tutorExitDateTo', params.tutorExitDateTo);
  if (params?.tutorExitReason) queryParams.append('tutorExitReason', params.tutorExitReason);

  const { data } = await apiClient.get(`/users/volunteers?${queryParams.toString()}`);
  return data;
};

/** `GET /users/volunteers/:identifier` */
export const getVolunteer = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/volunteers/${encodeURIComponent(identifier)}`);
  return data;
};

/** `GET /users/volunteers/export` — download XLSX. */
export const exportVolunteersExcel = async (params?: GetUsersParams): Promise<void> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.levelIds?.forEach((id) => queryParams.append('levelIds', id));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));

  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  const dateFrom = toQueryDate(params?.dateFrom);
  const dateTo = toQueryDate(params?.dateTo);
  if (dateFrom) queryParams.append('dateFrom', dateFrom);
  if (dateTo) queryParams.append('dateTo', dateTo);
  params?.availabilityDays?.forEach((d) => queryParams.append('availabilityDays', String(d)));
  params?.vscFrequencies?.forEach((f) => queryParams.append('vscFrequencies', f));
  if (params?.engagementStartDateFrom)
    queryParams.append('engagementStartDateFrom', params.engagementStartDateFrom);
  if (params?.engagementStartDateTo)
    queryParams.append('engagementStartDateTo', params.engagementStartDateTo);
  if (params?.engagementEndDateFrom)
    queryParams.append('engagementEndDateFrom', params.engagementEndDateFrom);
  if (params?.engagementEndDateTo)
    queryParams.append('engagementEndDateTo', params.engagementEndDateTo);
  if (params?.tutorExitDateFrom) queryParams.append('tutorExitDateFrom', params.tutorExitDateFrom);
  if (params?.tutorExitDateTo) queryParams.append('tutorExitDateTo', params.tutorExitDateTo);
  if (params?.tutorExitReason) queryParams.append('tutorExitReason', params.tutorExitReason);

  const response = await apiClient.get(`/users/volunteers/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `volunteers-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
